import {
  READ_ONLY_TOOLS,
  SUB_SYSTEM_PROMPT,
  SYSTEM_PROMPT,
  TOOLS,
  executeTool,
  newTurnGroup,
  setSubAgentRunner,
  workbookContext,
} from './tools'
import type { LaneEvent, ToolCtx } from './tools'
import { useAuth } from './auth'

export interface ToolCall {
  id: string
  type: 'function'
  function: { name: string; arguments: string }
}

export interface ChatAttachment {
  kind: 'image' | 'pdf' | 'spreadsheet'
  name: string
  mime: string
  previewUrl?: string
  dataUrl?: string
  text?: string
}

type MessageContent =
  | string
  | Array<
      | { type: 'text'; text: string }
      | { type: 'image_url'; image_url: { url: string } }
      | { type: 'file'; file: { filename: string; file_data: string } }
    >

export interface ChatMessage {
  role: 'user' | 'assistant' | 'tool'
  content: MessageContent
  tool_calls?: ToolCall[]
  tool_call_id?: string
  name?: string
}

export interface ToolTrace {
  name: string
  args: any
  ok: boolean
  summary: string
  /** 병렬 하위 작업자가 실행한 것이면 그 레인 이름 */
  lane?: string
}

export interface AgentEvents {
  onTool?: (t: ToolTrace) => void
  onLane?: (e: LaneEvent) => void
  onThinking?: (text: string) => void
}

const MAX_ROUNDS = 10
const SUB_MAX_ROUNDS = 8
// 하위 작업자에게서 뺀 것:
//  - run_parallel: 재귀 금지
//  - activate_sheet / select_range: 동시에 도는 레인들이 화면 포커스를 서로 뺏는다.
//    (활성 시트가 바뀌면 sheet 를 생략한 다른 레인의 도구가 엉뚱한 시트로 간다)
const SUB_EXCLUDED = new Set(['run_parallel', 'activate_sheet', 'select_range'])
const SUB_TOOLS = TOOLS.filter((t) => !SUB_EXCLUDED.has(t.function.name))

export const DEFAULT_MODEL = 'google/gemini-3.5-flash-lite'
const LS_MODEL = 'aisheet.model'

export function getModel(): string {
  return localStorage.getItem(LS_MODEL) || DEFAULT_MODEL
}
export function setModel(id: string) {
  localStorage.setItem(LS_MODEL, id)
}

function summarize(name: string, out: any): string {
  if (!out?.ok) return String(out?.error ?? out?.result ?? '실패')
  switch (name) {
    case 'read_range':
      return `${out.sheet}!${out.range} 읽음`
    case 'write_range':
      return `${out.sheet}!${out.written} (${out.cells}셀) 기록`
    case 'write_cells':
      return `${out.sheet} ${out.cells}셀 기록`
    case 'format_range':
      return `${out.sheet}!${out.formatted} 서식`
    case 'merge_cells':
      return `${out.sheet}!${out.merged} 병합`
    case 'unmerge_cells':
      return `${out.sheet}!${out.unmerged} 병합 해제`
    case 'clear_range':
      return `${out.sheet}!${out.cleared} 지움`
    case 'sort_range':
      return `${out.sheet}!${out.sorted} ${out.by} ${out.order} 정렬`
    case 'find_replace':
      return `${out.replaced_cells}셀 치환`
    case 'list_sheets':
      return `시트 ${out.sheets?.length ?? 0}개 조회`
    case 'run_parallel':
      return `${out.lanes}갈래 동시 실행 (동시 ${out.concurrency}, ${(out.elapsed_ms / 1000).toFixed(1)}s)`
    default:
      return (
        Object.entries(out)
          .filter(([k]) => k !== 'ok')
          .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v).slice(0, 40) : v}`)
          .join(' ') || '완료'
      )
  }
}

async function callServer(messages: any[], model: string, tools: any[]) {
  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ messages, tools, model }),
  })
  const data = await res.json().catch(() => ({ error: '응답을 해석할 수 없습니다.' }))
  if (!res.ok) throw new Error(data.error || `서버 오류 ${res.status}`)
  // 서버가 차감 후 잔액을 실어 보낸다 — UI 배지를 즉시 갱신
  if (typeof data.credits === 'number') useAuth.getState().setCredits(data.credits)
  return data as { message: any; finish_reason: string | null; model: string }
}

function parseArgs(call: ToolCall, truncated: boolean): { args: any; error?: string } {
  try {
    return { args: call.function.arguments ? JSON.parse(call.function.arguments) : {} }
  } catch {
    // finish_reason=length 면 인자 JSON 이 출력 한도에서 잘린 것 — 모델에게
    // "덜 쓰라"가 아니라 "나눠 쓰라"를 정확히 알려줘야 재시도가 성공한다.
    const reason = truncated
      ? '출력 토큰 한도로 인자 JSON이 중간에 잘렸습니다. 같은 작업을 더 작은 범위(예: 행 단위 여러 번의 write_range)로 나눠 다시 호출하세요.'
      : `arguments JSON 파싱 실패: ${call.function.arguments?.slice(0, 200)}`
    return { args: {}, error: reason }
  }
}

async function runOneCall(call: ToolCall, ctx: ToolCtx, truncated = false): Promise<{ out: any; args: any }> {
  const { args, error } = parseArgs(call, truncated)
  if (error) return { out: { ok: false, error }, args }
  try {
    return { out: await executeTool(call.function.name, args, ctx), args }
  } catch (e: any) {
    return { out: { ok: false, error: e?.message ?? String(e) }, args }
  }
}

// ── 병렬 하위 에이전트 ───────────────────────────────────────────────────────

setSubAgentRunner(async (task, ctx) => {
  const model = ctx.model || getModel()
  const messages: any[] = [
    { role: 'system', content: SUB_SYSTEM_PROMPT },
    { role: 'system', content: `[현재 워크북 상태]\n${workbookContext()}` },
    { role: 'user', content: task.instruction },
  ]
  const used: string[] = []

  for (let round = 0; round < SUB_MAX_ROUNDS; round++) {
    const { message, finish_reason } = await callServer(messages, model, SUB_TOOLS)
    const calls: ToolCall[] = message.tool_calls ?? []
    const text = typeof message.content === 'string' ? message.content : ''
    messages.push({ role: 'assistant', content: text, ...(calls.length ? { tool_calls: calls } : {}) })

    if (!calls.length) return { ok: true, summary: text || '완료', tools: used }

    // 하위 작업자는 자기 범위만 만지므로 같은 배치 안에서는 순서대로 처리한다
    for (const call of calls) {
      const { out } = await runOneCall(call, { ...ctx, onLane: undefined }, finish_reason === 'length')
      used.push(call.function.name)
      const payload = JSON.stringify(out)
      messages.push({
        role: 'tool',
        tool_call_id: call.id,
        content: payload.length > 24000 ? payload.slice(0, 24000) + '…(잘림)' : payload,
      })
    }
    messages[1] = { role: 'system', content: `[현재 워크북 상태]\n${workbookContext()}` }
  }
  return { ok: false, summary: '하위 작업이 라운드 한도에 도달했습니다.', tools: used }
})

// ── 메인 턴 ──────────────────────────────────────────────────────────────────

/**
 * 사용자 한 턴을 처리한다.
 * 도구 실행은 브라우저(워크북이 있는 쪽)에서 하고, 서버는 LLM 중계만 한다.
 * 한 배치 안의 도구가 전부 읽기 전용이면 동시에 실행한다.
 */
export async function runTurn(
  history: ChatMessage[],
  userText: string,
  events: AgentEvents = {},
  attachment?: ChatAttachment
): Promise<{ history: ChatMessage[]; reply: string; traces: ToolTrace[] }> {
  const group = newTurnGroup()
  const model = getModel()
  const traces: ToolTrace[] = []
  const ctx: ToolCtx = { group, model, onLane: events.onLane }
  const baseHistory = attachment ? history.map(withoutAttachments) : history

  const prompt = attachment?.kind === 'spreadsheet' && attachment.text
    ? `${userText}\n\n[첨부 스프레드시트: ${attachment.name}]\n${attachment.text}`
    : userText
  const userContent: MessageContent =
    attachment?.kind === 'image' && attachment.dataUrl
      ? [
          { type: 'text', text: prompt || '이 이미지를 분석해 주세요.' },
          { type: 'image_url', image_url: { url: attachment.dataUrl } },
        ]
      : attachment?.kind === 'pdf' && attachment.dataUrl
        ? [
            { type: 'text', text: prompt || '이 PDF 문서를 분석해 주세요.' },
            { type: 'file', file: { filename: attachment.name, file_data: attachment.dataUrl } },
          ]
        : prompt

  const working: any[] = [
    { role: 'system', content: SYSTEM_PROMPT },
    { role: 'system', content: `[현재 워크북 상태]\n${workbookContext()}` },
    ...baseHistory.map(stripForApi),
    { role: 'user', content: userContent },
  ]

  const nextHistory: ChatMessage[] = [...baseHistory, { role: 'user', content: userContent }]
  let reply = ''

  for (let round = 0; round < MAX_ROUNDS; round++) {
    const { message, finish_reason } = await callServer(working, model, TOOLS)
    const calls: ToolCall[] = message.tool_calls ?? []
    const truncated = finish_reason === 'length'
    const text = typeof message.content === 'string' ? message.content : ''

    working.push({ role: 'assistant', content: text, ...(calls.length ? { tool_calls: calls } : {}) })
    nextHistory.push({ role: 'assistant', content: text, ...(calls.length ? { tool_calls: calls } : {}) })

    if (text && calls.length) events.onThinking?.(text)

    if (!calls.length) {
      reply = truncated ? `${text}\n\n(응답이 길이 한도로 잘렸습니다)` : text
      break
    }

    // 전부 읽기 전용이면 동시 실행, 하나라도 쓰기가 섞이면 순서 보존
    const allReadOnly = calls.every((c) => READ_ONLY_TOOLS.has(c.function.name))
    const outcomes = allReadOnly
      ? await Promise.all(calls.map((c) => runOneCall(c, ctx, truncated)))
      : await calls.reduce(
          async (accP, c) => {
            const acc = await accP
            acc.push(await runOneCall(c, ctx, truncated))
            return acc
          },
          Promise.resolve([] as Array<{ out: any; args: any }>)
        )

    calls.forEach((call, i) => {
      const { out, args } = outcomes[i]
      const trace: ToolTrace = {
        name: call.function.name,
        args,
        ok: Boolean(out.ok),
        summary: summarize(call.function.name, out),
      }
      traces.push(trace)
      events.onTool?.(trace)

      const payload = JSON.stringify(out)
      const msg: ChatMessage = {
        role: 'tool',
        tool_call_id: call.id,
        name: call.function.name,
        content: payload.length > 60000 ? payload.slice(0, 60000) + '…(잘림)' : payload,
      }
      working.push(msg)
      nextHistory.push(msg)
    })

    // 도구 실행으로 시트가 바뀌었으니 스냅샷을 갱신해서 다음 라운드에 반영
    working[1] = { role: 'system', content: `[현재 워크북 상태]\n${workbookContext()}` }

    if (round === MAX_ROUNDS - 1) reply = text || '(도구 호출 한도에 도달해 중단했습니다.)'
  }

  return { history: nextHistory, reply, traces }
}

function withoutAttachments(m: ChatMessage): ChatMessage {
  if (m.role !== 'user' || !Array.isArray(m.content)) return m
  const text = m.content
    .filter((part): part is { type: 'text'; text: string } => part.type === 'text')
    .map((part) => part.text)
    .join('\n')
  return { ...m, content: `${text}\n[이전 첨부 파일은 새 첨부로 교체됨]`.trim() }
}

/** OpenRouter 로 보낼 때 UI 전용 필드를 떨궈낸다. */
function stripForApi(m: ChatMessage) {
  if (m.role === 'tool') return { role: 'tool', tool_call_id: m.tool_call_id, content: m.content }
  if (m.role === 'assistant')
    return { role: 'assistant', content: m.content, ...(m.tool_calls ? { tool_calls: m.tool_calls } : {}) }
  return { role: m.role, content: m.content }
}
